/**
 * Documents Service
 * Handles API calls for document management with server storage
 */

import { getApiUrl } from '../utils/apiConfig';

// Use appropriate URL based on environment
const isCodespaces = typeof window !== 'undefined' && window.location.hostname.includes('app.github.dev');
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ||
  (isCodespaces
    ? '' // Use relative URLs in Codespaces to go through Vite proxy
    : getApiUrl());

export interface Document {
  id: string;
  name: string;
  original_name: string;
  file_path: string;
  thumbnail_path: string | null;
  file_size: number;
  mime_type: string;
  project_id?: string;
  team_id: string;
  uploaded_by: string;
  tags: string[];
  description?: string;
  created_at: string;
  updated_at: string;
}

export interface UploadResponse {
  success: boolean;
  data?: Document[];
  message?: string;
  error?: string;
}

export interface DocumentsResponse {
  success: boolean;
  data?: Document[];
  count?: number;
  error?: string;
}

class DocumentsService {
  private teamId = 'team-zmfzbdu5angznj0642lb';

  /**
   * Get all documents for the team
   */
  async getDocuments(projectId?: string): Promise<Document[]> {
    try {
      const params = new URLSearchParams({
        team_id: this.teamId
      });

      if (projectId) {
        params.append('project_id', projectId);
      }

      const response = await fetch(`${API_BASE_URL}/api/documents?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: DocumentsResponse = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch documents');
      }

      return result.data || [];
    } catch (error) {
      console.error('Error fetching documents:', error);
      return [];
    }
  }

  /**
   * Upload documents to server
   */
  async uploadDocuments(
    files: FileList,
    projectId?: string,
    uploadedBy: string = 'user'
  ): Promise<Document[]> {
    try {
      const formData = new FormData();

      // Add files
      for (let i = 0; i < files.length; i++) {
        formData.append('documents', files[i]);
      }

      // Add metadata
      formData.append('team_id', this.teamId);
      formData.append('uploaded_by', uploadedBy);

      if (projectId) {
        formData.append('project_id', projectId);
      }

      const response = await fetch(`${API_BASE_URL}/api/documents/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: UploadResponse = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to upload documents');
      }

      return result.data || [];
    } catch (error) {
      console.error('Error uploading documents:', error);
      throw error;
    }
  }

  /**
   * Delete a document from server
   */
  async deleteDocument(documentId: string): Promise<boolean> {
    try {
      const params = new URLSearchParams({
        team_id: this.teamId
      });

      const response = await fetch(`${API_BASE_URL}/api/documents/${documentId}?${params}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      return result.success;
    } catch (error) {
      console.error('Error deleting document:', error);
      return false;
    }
  }

  /**
   * Get document URL for viewing/downloading
   */
  getDocumentUrl(filePath: string): string {
    if (filePath.startsWith('http')) {
      return filePath;
    }

    // Return full backend URL for uploaded files
    return `${API_BASE_URL}${filePath}`;
  }

  /**
   * Get thumbnail URL
   */
  getThumbnailUrl(thumbnailPath: string | null): string | null {
    if (!thumbnailPath) return null;
    return this.getDocumentUrl(thumbnailPath);
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Get file type extension
   */
  getFileExtension(fileName: string): string {
    return fileName.split('.').pop()?.toLowerCase() || 'file';
  }
}

export default new DocumentsService();